import { useState } from "react";
import { IonContent, IonPage } from "@ionic/react";

import { ActionBar } from "../components/ActionBar";
import { IconButton } from "../components/Button";
import { Chip } from "../components/Chip";
import { ConfirmSheet } from "../components/ConfirmSheet";
import { CycleRing } from "../components/CycleRing";
import { CycleSheet } from "../components/CycleSheet";
import { Hero } from "../components/Hero";
import { Insight } from "../components/Insight";
import { Notice } from "../components/Notice";
import { NavRow, RowGroup } from "../components/Rows";
import { Screen } from "../components/Shell";
import { SymptomMap } from "../components/SymptomMap";
import { TopBar } from "../components/TopBar";
import { I } from "../components/icons";
import {
  cycleAffected,
  cycleInfo,
  cycleOn,
  hormonal,
  nextPeriodEstimate,
  ovulationEstimate,
  phaseOf,
  type PhaseId,
} from "../domain/cycle";
import {
  cycleInsights,
  intakeByPhase,
  regularity,
  symptomMap,
  waterSwing,
  type CycleDeps,
  type Regularity,
} from "../domain/cycleView";
import { cycleRing, phasePanel, type PhasePanel } from "../domain/cycleRing";
import { diaryCount, patternsFor } from "../domain/cyclePatterns";
import {
  PANEL_WORDS,
  PATTERN_WORDS,
  diaryRowLine,
  expectedLine,
  notYetLine,
  patternLine,
} from "../domain/cycleWords";
import { dayLabel, daysBetween, todayStr } from "../domain/date";
import type { Insight as InsightData } from "../domain/insight";
import { entriesFor, foodOn } from "../domain/intake";
import { sessionKeyFor } from "../domain/plan";
import { energyView, loggedPeriods, readingsTagged, signed } from "../domain/progressView";
import { activeInWindow } from "../domain/training";
import { weightSeries } from "../domain/trend";
import type { KayaState } from "../domain/types";
import { useGo } from "../lib/useGo";
import { useKaya } from "../store/kayaStore";
import { setPeriodStart, setSessionOverride } from "../store/toggles";
import "./Progress.css";
import "./ProgressCycle.css";

const PHASE_WORD: Record<PhaseId, string> = {
  menstrual: "period",
  follicular: "follicular",
  ovulation: "ovulation",
  luteal: "luteal",
};

const NUM_WORDS = ["no", "one", "two", "three", "four", "five"];

/** What the other chapters' figures are, handed to the cycle builders (ADR-033). */
function cycleDeps(S: KayaState, today: string): CycleDeps {
  const weights = new Map(weightSeries(S).map((p) => [p.date, p.v]));
  const food = foodOn(S);
  const start = S.profile.startDate;
  const span = start && start <= today ? Math.min(28, daysBetween(start, today) + 1) : 28;
  return {
    weights: (ds) => weights.get(ds) ?? null,
    // today is still being eaten, so it is not a day's In yet
    intake: (ds) => {
      if (!food || ds >= today) return null;
      const e = entriesFor(S, ds);
      return e.length ? e.reduce((a, x) => a + x.kcal, 0) : null;
    },
    deficit: energyView(S, today, 28, (ds) => cycleAffected(S, ds)).deficit,
    activeMin: span > 0 ? Math.round((activeInWindow(S, today, 28) * 7) / span) : null,
  };
}

function pattern(reg: Regularity): { value: string; sub: string } {
  const n = NUM_WORDS[reg.last.length] ?? String(reg.last.length);
  switch (reg.verdict) {
    case "regular":
      return { value: "Regular", sub: `the last ${n} cycles within 2 days` };
    case "varies":
      return { value: "Varies", sub: `the last ${n} cycles ${reg.spread} days apart` };
    case "outside":
      return { value: "Outside 21–35", sub: `the last ${n} cycles: ${reg.last.join(", ")} days` };
    case "hormonal":
      return { value: "On a hormonal method", sub: "bleeds follow the method, not a cycle" };
    default:
      return { value: "–", sub: "shows once three periods are logged" };
  }
}

/**
 * The phase panel under the ring (ADR-059, SNIPPETS §51): what the phase is, what is good to do,
 * what to take care with, then what comes next. Every word is `phasePanel`'s, from cycleWords.ts.
 * The rows are keyed by the phase so a change of phase plays the kit's disclose motion.
 */
function PhasePanelView({ panel, onBack, go }: { panel: PhasePanel; onBack: () => void; go: (to: string) => void }) {
  return (
    <section className="k-group pc-panel" aria-label={panel.heading}>
      <div className="pc-panel__head">
        <h2 className="k-group__h">{panel.heading}</h2>
        {panel.isToday || !panel.next ? null : <Chip onClick={onBack}>{PANEL_WORDS.back}</Chip>}
      </div>
      <div className="k-rows pc-panel__rows" key={panel.id}>
        {panel.why ? <p className="pc-panel__cell pc-panel__p">{panel.why}</p> : null}
        <p className="pc-panel__cell pc-panel__p">{panel.what}</p>
        <div className="pc-panel__cell">
          <p className="pc-panel__t">{PANEL_WORDS.good}</p>
          <ul className="pc-panel__list">
            {panel.good.map((g) => (
              <li key={g}>{g}</li>
            ))}
          </ul>
        </div>
        <div className="pc-panel__cell">
          <p className="pc-panel__t">{PANEL_WORDS.easy}</p>
          <ul className="pc-panel__list">
            {panel.easy.map((g) => (
              <li key={g}>{g}</li>
            ))}
          </ul>
        </div>
        {panel.forYou ? (
          <div className="pc-panel__cell">
            <p className="pc-panel__t">{PATTERN_WORDS.forYou}</p>
            <ul className="pc-panel__list">
              {panel.forYou.map((g) => (
                <li key={g}>{g}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {panel.iron ? (
          <NavRow title={panel.iron.title} subtitle={panel.iron.line} icon={I.bowl} onClick={() => go("/progress/food")} />
        ) : null}
        {panel.next ? (
          <div className="pc-panel__cell">
            <p className="pc-panel__t">{panel.next.heading}</p>
            <p className="pc-panel__p">{panel.next.line}</p>
            <p className="pc-panel__gist">{panel.next.gist}</p>
          </div>
        ) : null}
      </div>
      {panel.note ? <p className="pg-note pc-panel__note">{panel.note}</p> : null}
    </section>
  );
}

function Insights({ items }: { items: (InsightData | undefined)[] }) {
  return (
    <>
      {items
        .filter((x): x is InsightData => !!x)
        .map((x) => (
          <Insight key={x.key} insight={x} />
        ))}
    </>
  );
}

/**
 * /progress/cycle — the whole cycle (ADR-033): where today sits and what comes next as estimates,
 * the pre-period week, regularity, the scale, appetite and energy by phase, the symptom map,
 * period-day training, the logged dates (removable, as a tombstoned removal through toggles.ts —
 * ADR-019) and the quiet signposts. Nothing here renders with tracking off.
 */
export default function ProgressCycle() {
  const S = useKaya((s) => s.S);
  const update = useKaya((s) => s.update);
  const showToast = useKaya((s) => s.showToast);
  const [doomed, setDoomed] = useState<string | null>(null);
  const [logOpen, setLogOpen] = useState(false);
  // the phase the panel shows; null is today's
  const [shown, setShown] = useState<PhaseId | null>(null);
  const go = useGo();

  const today = todayStr();
  const back = { to: "/progress", label: "Back to Progress" };

  if (!cycleOn(S))
    return (
      <IonPage>
        <IonContent>
          <Screen>
            <TopBar title="Cycle" back={back} />
            <div className="pg">
              <Notice tone="info" secondary={{ label: "Open You › Cycle", onClick: () => go("/you/cycle") }}>
                Cycle tracking is off. Turn it on in You › Cycle and this page fills in.
              </Notice>
            </div>
          </Screen>
        </IonContent>
      </IonPage>
    );

  const deps = cycleDeps(S, today);
  const insights = cycleInsights(S, today, deps);
  const pick = (k: string) => insights.find((x) => x.key === k);

  const ci = cycleInfo(S, today);
  const horm = hormonal(S);
  const len = S.cycle.length || 28;
  const ph = phaseOf(S, today);
  const next = nextPeriodEstimate(S, today);
  const ov = ovulationEstimate(S, today);
  const reg = regularity(S, today);
  const pat = pattern(reg);
  const map = symptomMap(S, today);
  const logged = loggedPeriods(S, today);
  const tagged = readingsTagged(S, (ds) => cycleAffected(S, ds), today);
  const appetite = intakeByPhase(S, today, deps.intake);

  const heroLine =
    ci?.day == null
      ? "No period logged yet. Log one and predictions start from it."
      : [
          ph ? PHASE_WORD[ph] : null,
          ci.last ? `started ${dayLabel(ci.last)}` : null,
          ov && ov >= today ? `ovulation ~${dayLabel(ov)} est.` : null,
          next ? `${horm ? "next bleed" : "next period"} ~${dayLabel(next)} est.` : null,
        ]
          .filter(Boolean)
          .join(" · ");

  // The pre-period notice offers to put the gentle session on the predicted first day. A day that
  // is already rest or gentle has nothing to swap; one already swapped says so by the button going.
  const nextNote = pick("cycle.next");
  const nextKey = next ? sessionKeyFor(S, next) : null;
  const canPlan = !!next && nextKey !== "P" && nextKey !== "R";
  const planGentle = () => {
    if (!next) return;
    update((d) => setSessionOverride(d, next, "P"));
    showToast(`Gentle session planned for ${dayLabel(next)}`);
  };

  const remove = () => {
    if (!doomed) return;
    const ds = doomed;
    update((d) => setPeriodStart(d, ds, false));
    setDoomed(null);
    showToast("Removed");
  };

  const symptomsInsight = pick("cycle.symptoms");
  const trainingInsight = pick("cycle.training");
  // pain days and what she did on them — her pattern beside the rule (ADR-057)
  const painInsight = pick("cycle.pain");
  const worth = [pick("cycle.flags"), pick("cycle.reds")].filter(Boolean);
  const appetiteGroup = [pick("cycle.appetite"), pick("cycle.energy")].filter(Boolean);
  const water = pick("cycle.water");
  const hasMap = !!map && map.rows.length > 0;
  const ring = cycleRing(S, today);
  const panel = phasePanel(S, today, shown, waterSwing(S, today, deps.weights)?.kg);
  // her own patterns (ADR-060): a row each, the ones still waiting, then the diary
  const { patterns, notYet } = patternsFor(S, today);
  const diary = diaryCount(S, today);
  const cycleDay = ci?.last ? daysBetween(ci.last, today) + 1 : null;

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <TopBar title="Cycle" back={back} />
          <div className="pg">
            <Hero
              label="Cycle day"
              figure={ci?.day ?? null}
              unit={`of ~${len}`}
              line={heroLine}
              visual={
                ring ? <CycleRing ring={ring} selected={ring.phased ? (panel?.id ?? null) : null} onSelect={setShown} /> : undefined
              }
            />

            {panel ? <PhasePanelView panel={panel} onBack={() => setShown(null)} go={go} /> : null}

            {nextNote ? (
              <Notice
                tone="cycle"
                secondary={canPlan ? { label: "Add to Today", onClick: planGentle } : undefined}
              >
                {nextNote.text}
              </Notice>
            ) : null}

            <RowGroup heading="Your cycle">
              <NavRow
                kind="value"
                title="Average cycle"
                subtitle={reg.avgFromLog ? undefined : "your setting, until two are logged"}
                value={`${reg.avgCycle} days`}
              />
              <NavRow kind="value" title="Average period" value={`${reg.avgPeriod} days`} />
              <NavRow kind="value" title="Pattern" subtitle={pat.sub} value={pat.value} />
              <Insights items={[pick("cycle.regular")]} />
            </RowGroup>

            {water ? (
              <RowGroup heading="The scale and your cycle">
                <Insight insight={water} first />
              </RowGroup>
            ) : null}

            {appetiteGroup.length ? (
              <RowGroup heading="Appetite and energy">
                {appetiteGroup.map((x, i) => (x ? <Insight key={x.key} insight={x} first={i === 0} /> : null))}
              </RowGroup>
            ) : null}

            {hasMap || trainingInsight ? (
              <RowGroup heading={hasMap ? "Symptoms by cycle day" : "Period days"}>
                {map && hasMap ? (
                  <div className="pc-map">
                    <SymptomMap
                      rows={map.rows.map((r) => ({
                        key: r.id,
                        label: r.label,
                        cells: r.cells.map((v) => Math.max(0, Math.min(3, Math.round(v ?? 0))) as 0 | 1 | 2 | 3),
                      }))}
                      today={map.todayDay}
                      cycleLen={map.len}
                      ovulation={map.ovulationDay}
                      ariaLabel={`Symptoms by cycle day over ${map.cycles} ${map.cycles === 1 ? "cycle" : "cycles"}`}
                    />
                    <p className="pc-map__ev">
                      based on {map.cycles} {map.cycles === 1 ? "cycle" : "cycles"} of symptoms logged
                    </p>
                  </div>
                ) : null}
                {hasMap ? (
                  <Insights items={[patterns.length ? undefined : symptomsInsight, trainingInsight, painInsight]} />
                ) : trainingInsight ? (
                  <>
                    <Insight insight={trainingInsight} first />
                    {painInsight ? <Insight insight={painInsight} /> : null}
                  </>
                ) : null}
              </RowGroup>
            ) : null}

            <div className="pc-patterns">
            <RowGroup heading={PATTERN_WORDS.heading}>
              {patterns.map((p) => (
                <NavRow
                  key={p.row}
                  icon={I.drop}
                  tone="cycle"
                  title={p.label}
                  subtitle={[patternLine(p), expectedLine(p, cycleDay)].filter(Boolean).join(" · ")}
                  to={`/progress/cycle/diary/${p.row}`}
                />
              ))}
              {notYet.map((n, i) => {
                const w = notYetLine(n);
                return (
                  <Insight
                    key={n.row}
                    first={!patterns.length && i === 0}
                    insight={{
                      key: `cycle.wait.${n.row}`,
                      text: w.text,
                      basedOn: w.basedOn,
                      wait: w.wait,
                    }}
                  />
                );
              })}
              {diary.days && !patterns.length && !notYet.length ? (
                <Insight
                  first
                  insight={{
                    key: "cycle.patterns",
                    text: PATTERN_WORDS.none,
                    basedOn: PATTERN_WORDS.logged(diary.days),
                    wait: PATTERN_WORDS.noneWait,
                  }}
                />
              ) : null}
              <NavRow
                icon={I.list}
                title={PATTERN_WORDS.diary}
                subtitle={diary.days ? diaryRowLine(diary.days, diary.cycles) : PATTERN_WORDS.nothing}
                to="/progress/cycle/diary"
              />
            </RowGroup>
            </div>

            <RowGroup heading="Logged periods">
              {logged.length ? (
                logged.map((p) => (
                  <NavRow
                    key={p.ds}
                    kind="action"
                    icon={I.drop}
                    title={dayLabel(p.ds)}
                    subtitle={
                      [
                        p.current ? `this cycle · day ${p.day}` : p.length !== null ? `${p.length} days` : null,
                        p.ended ? `ended ${dayLabel(p.ended)}` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ") || undefined
                    }
                    action={
                      <IconButton
                        icon={I.close}
                        label={`Remove the period logged on ${dayLabel(p.ds)}`}
                        onClick={() => setDoomed(p.ds)}
                      />
                    }
                  />
                ))
              ) : (
                <NavRow kind="value" title="None logged yet" subtitle="Log today and a start lands here." />
              )}
            </RowGroup>
            {logged.length ? <p className="pg-note pc-note">Removing a date asks first, and syncs as a removal.</p> : null}

            {worth.length ? (
              <RowGroup heading="Worth knowing">
                {worth.map((x, i) => (x ? <Insight key={x.key} insight={x} first={i === 0} /> : null))}
              </RowGroup>
            ) : null}

            <RowGroup>
              <NavRow
                title="Cycle settings"
                subtitle="length, period length, contraception"
                icon={I.edit}
                to="/you/cycle"
              />
            </RowGroup>

            {logged.length ? (
              <RowGroup heading="Connected">
                <NavRow
                  title="Body"
                  subtitle={
                    tagged.count
                      ? `${tagged.count} ${tagged.count === 1 ? "weigh-in" : "weigh-ins"} tagged cycle-affected`
                      : "no weigh-ins in the days around a period yet"
                  }
                  icon={I.scale}
                  to="/progress/body"
                />
                {foodOn(S) ? (
                  <NavRow
                    title="Food"
                    subtitle={
                      appetite
                        ? `${signed(Math.round(appetite.diff / 10) * 10)} kcal before a period`
                        : "intake in the pre-period week"
                    }
                    icon={I.bowl}
                    to="/progress/food"
                  />
                ) : null}
                <NavRow
                  title="Training"
                  subtitle={horm ? "period-day sessions" : "energy by phase"}
                  icon={I.dumbbell}
                  to="/progress/training"
                />
              </RowGroup>
            ) : null}
          </div>

          <ActionBar primary={{ label: "Log today", icon: I.plus, onClick: () => setLogOpen(true) }} />
        </Screen>

        <CycleSheet open={logOpen} onClose={() => setLogOpen(false)} ds={today} />
        {doomed ? (
          <ConfirmSheet
            title={`Remove the ${dayLabel(doomed)} period?`}
            sub="Predictions go back to the period before it."
            confirm={{ label: "Remove date", onClick: remove }}
            cancel="Keep it"
            onClose={() => setDoomed(null)}
          >
            <p className="pg-note">It syncs to your other devices as a removal, not just a gap.</p>
          </ConfirmSheet>
        ) : null}
      </IonContent>
    </IonPage>
  );
}

